import circuit from "../assets/pin_circuit.svg"
import pin from "../assets/pushpin-pink.png"

const Privacy = () => {
    return (
        <>
        <section className="my-10 space-y-2">
                <div className="mt-10 md:mx-24 md:flex justify-between">
                    <h1 className="text-center text-2xl md:text-3xl min-w-max">PRIVACY POLICY</h1>
                    <div className="flex items-center">
                        <img src={circuit} className="h-12 scale-[2] mix-blend-screen translate-y-[4.5rem] translate-x-3"/>
                        <img src={pin} className="w-12 scale-[2.5] mix-blend-screen"/>
                    </div>
                </div>
                <div className="mx-4 md:mx-24 space-y-4">
                    <p className="font-bold opacity-50">Last updated July 29, 2025</p>
                    <br/>
                    <div className="opacity-80 space-y-4">
                        <p>
                            This Privacy Notice for Boundless Gamers (doing business as Pixel Games Studio) ("<b>we</b>," "<b>us</b>," or
                            "<b>our</b>"), describes how and why we might access, collect, store, use, and/or share ("<b>process</b>") your
                            personal information when you use our services ("<b>Services</b>"), including when you:
                        </p>
                        <ul className="list-outside list-disc ml-8 space-y-4">
                            <li>
                                Visit our website at boundlessgamers.org or any website of ours that links to this Privacy Notice
                            </li>
                            <li>
                                Use Boundless Gamers. A charity organization that helps the public by educating
                                them on how gaming can play a pivotal role in medical treatments. In our mission,
                                we develop games for charity. We accept donations to further promote our mission
                                goals and to support game development.
                            </li>
                            <li>
                                Engage with us in other related ways, including any sales, marketing, or events
                            </li>
                        </ul>
                        <p>
                            <b>Questions or concerns?</b> Reading this Privacy Notice will help you understand your privacy rights
                            and choices. We are responsible for making decisions about how your personal information is
                            processed. If you do not agree with our policies and practices, please do not use our Services. If you
                            still have any questions or concerns, please contact us at admin@boundlessgamers.org.
                        </p>

                    </div>
                    <h3 className="text-2xl font-bold">SUMMARY OF KEY POINTS</h3>
                    <div className="opacity-80 space-y-4">
                        <p className="font-bold italic">
                        This summary provides key points from our Privacy Notice, but you can find out more details
about any of these topics by clicking the link following each key point or by using our table of
contents below to find the section you are looking for.
                    </p>
                    <p>
                        <b>What personal information do we process? </b>
                        When you visit, use, or navigate our Services, we
may process personal information depending on how you interact with us and the Services, the
choices you make, and the products and features you use. Learn more about personal information
you disclose to us.
                    </p>
                    <p>
                        <b>Do we process any sensitive personal information? </b>
                        Some of the information may be
                        considered "special" or "sensitive" in certain jurisdictions, for example your racial or ethnic origins,
                        sexual orientation, and religious beliefs. We may process sensitive personal information when
                        necessary with your consent or as otherwise permitted by applicable law. Learn more
                        about sensitive information we process.
                    </p>
                    <p>
                        <b>Do we collect any information from third parties? </b>
                        We do not collect any information from third
parties.
                    </p>
                    <p>
                        <b>How do we process your information? </b>
                        We process your information to provide, improve, and
                        administer our Services, communicate with you, for security and fraud prevention, and to comply
                        with law. We may also process your information for other purposes with your consent. We process
                        your information only when we have a valid legal reason to do so. Learn more about how we
                        process your information.
                    </p>
                    <p>
                        <b>In what situations and with which parties do we share personal information? </b>
                        We may share
                        information in specific situations and with specific third parties. Learn more about when and with
                        whom we share your personal information.
                    </p>
                    <p>
                        <b>How do we keep your information safe? </b>
                        We have adequate organizational and technical
                        processes and procedures in place to protect your personal information. However, no electronic
                        transmission over the internet or information storage technology can be guaranteed to be 100%
                        secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third
                        parties will not be able to defeat our security and improperly collect, access, steal, or modify your
                        information. Learn more about how we keep your information safe.
                    </p>
                    <p>
                        <b>What are your rights? </b>
                        Depending on where you are located geographically, the applicable privacy
                        law may mean you have certain rights regarding your personal information. Learn more about your
                        privacy rights.
                    </p>
                    <p>
                        <b>How do you exercise your rights? </b>
                        The easiest way to exercise your rights is by submitting a data
                        subject access request, or by contacting us. We will consider and act upon any request in
                        accordance with applicable data protection laws.
                    </p>
                    <p>
                        Want to learn more about what we do with any information we collect? Review the Privacy Notice in full.
                    </p>
                    </div>
                    
                    <h3 className="font-bold text-2xl">TABLE OF CONTENTS</h3>
                    <ol className="list-outside ml-8 list-decimal">
                        <li>OUR SERVICES</li>
                        <li>INTELLECTUAL PROPERTY RIGHTS</li>
                        <li>USER REPRESENTATIONS</li>
                        <li>PROHIBITED ACTIVITIES</li>
                        <li>USER GENERATED CONTRIBUTIONS</li>
                        <li>CONTRIBUTION LICENSE</li>
                        <li>SERVICES MANAGEMENT</li>
                        <li>TERM AND TERMINATION</li>
                        <li>MODIFICATIONS AND INTERRUPTIONS</li>
                        <li>GOVERNING LAW</li>
                        <li>DISPUTE RESOLUTION</li>
                        <li>CORRECTIONS</li>
                        <li>DISCLAIMER</li>
                        <li>LIMITATIONS OF LIABILITY</li>
                        <li>INDEMNIFICATION</li>
                        <li>USER DATA</li>
                        <li>ELECTRONIC COMMUNICATIONS, TRANSACTIONS, AND SIGNATURES</li>
                        <li>MISCELLANEOUS</li>
                        <li>CONTACT US</li>
                    </ol>
                    <h3 className="font-bold text-2xl">1. WHAT INFORMATION DO WE COLLECT?</h3>
                    <h4 className="font-bold text-xl">Personal information you disclose to us</h4>
                    <div className="opacity-80 space-y-4">
                        <p className="italic">
                        <b>In Short:</b> We collect personal information that you provide to us.
                    </p>
                    <p>
                        We collect personal information that you voluntarily provide to us when you express an interest in
obtaining information about us or our products and Services, when you participate in activities on the
Services, or otherwise when you contact us.
                    </p>
                    <p>
                        <b>Personal Information Provided by You. </b>
                        The personal information that we collect depends on the
                        context of your interactions with us and the Services, the choices you make, and the products and
                        features you use. The personal information we collect may include the following:
                    </p>
                    <ul className="list-outside list-disc ml-8 space-y-4">
                        <li>names</li>
                        <li>email addresses</li>
                        <li>phone numbers</li>
                        <li>mailing addresses</li>
                    </ul>
                    <p>
                        <b>Sensitive Information. </b>
                        When necessary, with your consent or as otherwise permitted by applicable
                        law, we process the following categories of sensitive information: <br/>
                        <b>Social Media Login Data. </b>
                        We may provide you with the option to register with us using your
                        existing social media account details, like your Facebook, X, or other social media account. If you
                        choose to register in this way, we will collect certain profile information about you from the social
                        media provider, as described in the section called "HOW DO WE HANDLE YOUR SOCIAL
                        LOGINS?" below.
                    </p>
                    <p>
                        All personal information that you provide to us must be true, complete, and accurate, and you must
                        notify us of any changes to such personal information.
                    </p>
                    </div>
                    
                    <h3 className="font-bold text-2xl">2. HOW DO WE PROCESS YOUR INFORMATION?</h3>
                    
                    <div className="opacity-80 space-y-4">
                        <p className="italic">
                        <b>In Short: </b>We process your information to provide, improve, and administer our Services,
communicate with you, for security and fraud prevention, and to comply with law. We process the
personal information for the following purposes listed below. We may also process your information
for other purposes only with your prior explicit consent.
                    </p>
                    <p className="font-bold">
                        We process your personal information for a variety of reasons, depending on how you
interact with our Services, including:
                    </p>
                    <ul className="list-outside list-disc ml-8 space-y-4">
                        <li>
                            <b>To save or protect an individual's vital interest.</b> We may process your information
                    when necessary to save or protect an individual's vital interest, such as to prevent
                    harm.
                        </li>
                    </ul>
                    </div>
                    
                    <h3 className="font-bold text-2xl">3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR
INFORMATION?</h3>
                    <div className="opacity-80 space-y-4">
                        <p className="italic">
                        <b>In Short: </b>We only process your personal information when we believe it is necessary and we have a
valid legal reason (i.e., legal basis) to do so under applicable law, like with your consent, to comply
with laws, to provide you with services to enter into or fulfill our contractual obligations, to protect
your rights, or to fulfill our legitimate business interests.</p>
                    
                    <p className="font-bold italic underline">If you are located in the EU or UK, this section applies to you.</p>
                    <p>
                        The General Data Protection Regulation (GDPR) and UK GDPR require us to explain the valid legal
bases we rely on in order to process your personal information. As such, we may rely on the
following legal bases to process your personal information:
                    </p>
                    <ul className="list-outside list-disc ml-8 space-y-4">
                        <li>
                            <b>Consent. </b>
                            We may process your information if you have given us permission (i.e., consent) to
use your personal information for a specific purpose. You can withdraw your consent at any
time. Learn more about withdrawing your consent.
                        </li>
                        <li>
                            <b>Legal Obligations. </b>
                            We may process your information where we believe it is
necessary for compliance with our legal obligations, such as to cooperate with a law
enforcement body or regulatory agency, exercise or defend our legal rights, or
disclose your information as evidence in litigation in which we are involved.
                        </li>
                        <li>
                            <b>Vital Interests. </b>
                            We may process your information where we believe it is necessary to
protect your vital interests or the vital interests of a third party, such as situations
involving potential threats to the safety of any person.
                        </li>
                    </ul>

                    <p className="font-bold underline italic">If you are located in Canada, this section applies to you.</p>
                    <p>We may process your information if you have given us specific permission (i.e., express
consent) to use your personal information for a specific purpose, or in situations where your
permission can be inferred (i.e., implied consent). You can withdraw your consent at any
time.</p>
                    <p>In some exceptional cases, we may be legally permitted under applicable law to process
your information without your consent, including, for example:</p>

                    <ul className="list-outside list-disc ml-8 space-y-4">
                        <li>If collection is clearly in the interests of an individual and consent cannot be obtained
in a timely way</li>
                        <li>For investigations and fraud detection and prevention</li>
                        <li>For business transactions provided certain conditions are met</li>
                        <li>If it is contained in a witness statement and the collection is necessary to assess,
process, or settle an insurance claim</li>
                        <li>For identifying injured, ill, or deceased persons and communicating with next of kin</li>
                        <li>If we have reasonable grounds to believe an individual has been, is, or may be
victim of financial abuse</li>
                        <li>If it is reasonable to expect collection and use with consent would compromise the
availability or the accuracy of the information and the collection is reasonable for
purposes related to investigating a breach of an agreement or a contravention of the
laws of Canada or a province</li>
                        <li>If disclosure is required to comply with a subpoena, warrant, court order, or rules of
the court relating to the production of records</li>
                        <li>If it was produced by an individual in the course of their employment, business, or profession
and the collection is consistent with the purposes for which the information was produced</li>
                        <li>If the collection is solely for journalistic, artistic, or literary purposes</li>
                        <li>If the information is publicly available and is specified by the regulations</li>
                        <li>We may disclose de-identified information for approved research or statistics
projects, subject to ethics oversight and confidentiality commitments</li>

                    </ul>

                    </div>
                    
                    <h3 className="font-bold text-2xl">4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL
INFORMATION?</h3>
                    <div className="opacity-80 space-y-4">
                        <p className="italic">
                        <b>In Short: </b>
                        We may share information in specific situations described in this section and/or with the
following third parties.
                    </p>
                    <p>
                        We may need to share your personal information in the following situations:
                    </p>
                    <ul className="list-outside list-disc ml-8 space-y-4">
                        <li>
                            <b>Business Transfers. </b>
                            We may share or transfer your information in connection with, or
during negotiations of, any merger, sale of company assets, financing, or acquisition of all or
a portion of our business to another company.
                        </li>
                        <li>
                            <b>Affiliates. </b>
                            We may share your information with our affiliates, in which case we will require
those affiliates to honor this Privacy Notice. Affiliates include our parent company and any
subsidiaries, joint venture partners, or other companies that we control or that are under
common control with us.
                        </li>
                        <li>
                            <b>Business Partners. </b>
                            We may share your information with our business partners to
offer you certain products, services, or promotions.
                        </li>
                    </ul>
                    </div>
                    
                    <h3 className="font-bold text-2xl">5. WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?</h3>
                    
                    <div className="opacity-80 space-y-4">
                        <p className="italic">
                        <b>In Short: </b>
                        We are not responsible for the safety of any information that you share with third parties
that we may link to or who advertise on our Services, but are not affiliated with, our Services.
                    </p>
                    <p>
                        The Services may link to third-party websites, online services, or mobile applications and/or contain
advertisements from third parties that are not affiliated with us and which may link to other websites,
services, or applications. Accordingly, we do not make any guarantee regarding any such third
parties, and we will not be liable for any loss or damage caused by the use of such third-party
websites, services, or applications. The inclusion of a link towards a third-party website, service, or
application does not imply an endorsement by us. We cannot guarantee the safety and privacy of
data you provide to any third-party websites. Any data collected by third parties is not covered by
this Privacy Notice. We are not responsible for the content or privacy and security practices and
policies of any third parties, including other websites, services, or applications that may be linked to
or from the Services. You should review the policies of such third parties and contact them directly to
respond to your questions.
                    </p>
                    </div>
                    
                    <h3 className="font-bold text-2xl">6. HOW DO WE HANDLE YOUR SOCIAL LOGINS?</h3>
                    
                    <div className="opacity-80 space-y-4">
                        <p className="italic">
                        <b>In Short: </b>
                        If you choose to register or log in to our Services using a social media account, we may
have access to certain information about you.
                    </p>
                    <p>
                        Our Services offer you the ability to register and log in using your third-party social media account
details (like your Facebook or X logins). Where you choose to do this, we will receive certain profile
information about you from your social media provider. The profile information we receive may vary
depending on the social media provider concerned, but will often include your name, email address,
friends list, and profile picture, as well as other information you choose to make public on such a
social media platform.
                    </p>
                    <p>
                        We will use the information we receive only for the purposes that are described in this Privacy Notice
or that are otherwise made clear to you on the relevant Services. Please note that we do not control,
and are not responsible for, other uses of your personal information by your third-party social media
provider. We recommend that you review their privacy notice to understand how they collect, use,
and share your personal information, and how you can set your privacy preferences on their sites
and apps.
                    </p>
                    </div>
                    
                    <h3 className="font-bold text-2xl">7. HOW LONG DO WE KEEP YOUR INFORMATION?</h3>
                    
                    <div className="opacity-80 space-y-4">
                        <p className="italic">
                        <b>In Short: </b>
                        We keep your information for as long as necessary to fulfill the purposes outlined in this
Privacy Notice unless otherwise required by law.
                    </p>
                    <p>
                        We will only keep your personal information for as long as it is necessary for the purposes set out in
this Privacy Notice, unless a longer retention period is required or permitted by law (such as tax,
accounting, or other legal requirements). No purpose in this notice will require us keeping your
personal information for longer than 6 months.
                    </p>
                    <p>
                        When we have no ongoing legitimate business need to process your personal information, we will
either delete or anonymize such information, or, if this is not possible (for example, because your
personal information has been stored in backup archives), then we will securely store your personal
information and isolate it from any further processing until deletion is possible.
                    </p>
                    </div>
                    
                    <h3 className="font-bold text-2xl">8. HOW DO WE KEEP YOUR INFORMATION SAFE?</h3>
                    
                    <div className="opacity-80 space-y-4">
                        <p className="italic">
                        <b>In Short: </b>
                         We aim to protect your personal information through a system of organizational and
technical security measures.
                    </p>
                    <p>
                        We have implemented appropriate and reasonable technical and organizational security measures
designed to protect the security of any personal information we process. However, despite our
safeguards and efforts to secure your information, no electronic transmission over the Internet or
information storage technology can be guaranteed to be 100% secure, so we cannot promise or
guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat
our security and improperly collect, access, steal, or modify your information. Although we will do our
best to protect your personal information, transmission of personal information to and from our
Services is at your own risk. You should only access the Services within a secure environment.
                    </p>
                    </div>
                    
                    <h3 className="font-bold text-2xl">9. WHAT ARE YOUR PRIVACY RIGHTS?</h3>
                    
                    <div className="opacity-80 space-y-4">
                        <p className="italic">
                        <b>In Short: </b>
                        Depending on your state of residence in the US or in some regions, such as the European
Economic Area (EEA), United Kingdom (UK), Switzerland, and Canada, you have rights that allow
you greater access to and control over your personal information. You may review, change, or
terminate your account at any time, depending on your country, province, or state of residence.
                    </p>
                    <p>
                        In some regions (like the EEA, UK, Switzerland, and Canada), you have certain rights under
applicable data protection laws. These may include the right (i) to request access and obtain a copy
of your personal information, (ii) to request rectification or erasure; (iii) to restrict the processing of
your personal information; (iv) if applicable, to data portability; and (v) not to be subject to automated
decision-making. If a decision that produces legal or similarly significant effects is made solely by
automated means, we will inform you, explain the main factors, and offer a simple way to request
human review. In certain circumstances, you may also have the right to object to the processing of
your personal information. You can make such a request by contacting us by using the contact
details provided in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" below.
                    </p>
                    <p>
                        We will consider and act upon any request in accordance with applicable data protection laws.
                    </p>
                    <p>
                        If you are located in the EEA or UK and you believe we are unlawfully processing your personal
information, you also have the right to complain to your Member State data protection
authority or UK data protection authority.
                    </p>
                    <p>
                        If you are located in Switzerland, you may contact the Federal Data Protection and Information
Commissioner.
                    </p>
                    <p>
                        <b className="underline">Withdrawing your consent: </b>
                        If we are relying on your consent to process your personal
information, which may be express and/or implied consent depending on the applicable law, you
have the right to withdraw your consent at any time. You can withdraw your consent at any time by
contacting us by using the contact details provided in the section "HOW CAN YOU CONTACT US
ABOUT THIS NOTICE?" below.
                    </p>
                    <p>
                        However, please note that this will not affect the lawfulness of the processing before its withdrawal
nor, when applicable law allows, will it affect the processing of your personal information conducted
in reliance on lawful processing grounds other than consent.
                    </p>
                    <p>
                        <b className="underline">Opting out of marketing and promotional communications: </b>
                         You can unsubscribe from our
marketing and promotional communications at any time by clicking on the unsubscribe link in the
emails that we send, or by contacting us using the details provided in the section "HOW CAN YOU
CONTACT US ABOUT THIS NOTICE?" below. You will then be removed from the marketing lists.
However, we may still communicate with you — for example, to send you service-related messages
that are necessary for the administration and use of your account, to respond to service requests, or
for other non-marketing purposes.
                    </p>
                    <p>
                        If you have questions or comments about your privacy rights, you may email us
at admin@boundlessgamers.org.
                    </p>
                    </div>
                    
                    <h3 className="font-bold text-2xl">10. CONTROLS FOR DO-NOT-TRACK FEATURES</h3>
                    
                    <div className="opacity-80 space-y-4">
                        <p>
                        Most web browsers and some mobile operating systems and mobile applications include a Do-Not-
                        Track ("DNT") feature or setting you can activate to signal your privacy preference not to have data
                        about your online browsing activities monitored and collected. At this stage, no uniform technology
                        standard for recognizing and implementing DNT signals has been finalized. As such, we do not
                        currently respond to DNT browser signals or any other mechanism that automatically communicates
                        your choice not to be tracked online. If a standard for online tracking is adopted that we must follow
                        in the future, we will inform you about that practice in a revised version of this Privacy Notice.
                    </p>
                    <p>
                        California law requires us to let you know how we respond to web browser DNT signals. Because
                        there currently is not an industry or legal standard for recognizing or honoring DNT signals, we do
                        not respond to them at this time.
                    </p>
                    </div>
                    

                    <h3 className="font-bold text-2xl">11. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</h3>
                    
                    <div className="opacity-80 space-y-4">
                        <p className="italic">
                        <b>In Short: </b>
                         If you are a resident of California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa,
Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode
Island, Tennessee, Texas, Utah, or Virginia, you may have the right to request access to and receive
details about the personal information we maintain about you and how we have processed it, correct
inaccuracies, get a copy of, or delete your personal information. You may also have the right to
withdraw your consent to our processing of your personal information. These rights may be limited in
some circumstances by applicable law. More information is provided below.
                    </p>
                    </div>
                    
                    <h4 className="font-bold text-xl">Categories of Personal Information We Collect</h4>
                    <div className="opacity-80 space-y-4">
                        <p>
                        The table below shows the categories of personal information we have collected in the past twelve
(12) months. The table includes illustrative examples of each category and does not reflect the
personal information we collect from you. For a comprehensive inventory of all personal information
we process, please refer to the section "WHAT INFORMATION DO WE COLLECT?"
                    </p>
                    <table>
                        <tr className="font-bold">
                            <th>Category</th>
                            <th>Examples</th>
                            <th>Collected</th>
                        </tr>
                        <tr>
                            <td>A. Identifiers</td>
                            <td>Contact details, such as real name, alias, postal
address, telephone or mobile contact number,
unique personal identifier, online identifier, Internet
Protocol address, email address, and account name</td>
                            <td>NO</td>
                        </tr>
                        <tr>
                            <td>B. Personal information as
defined in the California
Customer Records statute</td>
                            <td>
                                Name, contact information, education, employment,
employment history, and financial information
                            </td>
                            <td>NO</td>
                        </tr>
                        <tr>
                            <td>C. Protected classification
                                characteristics under state or
                                federal law</td>
                            <td>Gender, age, date of birth, race and ethnicity,
national origin, marital status, and other
demographic data </td>
                            <td>NO</td>
                        </tr>
                        <tr>
                            <td>D. Commercial information</td>
                            <td>Transaction information, purchase history, financial
details, and payment information </td>
                            <td>NO</td>
                        </tr>
                        <tr>
                            <td>E. Biometric information</td>
                            <td>Fingerprints and voiceprints </td>
                            <td>NO</td>
                        </tr>
                        <tr>
                            <td>F. Internet or other similar
network activity</td>
                            <td>Browsing history, search history, online behavior,
interest data, and interactions with our and other
websites, applications, systems, and
advertisements</td>
                            <td>NO</td>
                        </tr>
                        <tr>
                            <td>G. Geolocation data</td>
                            <td>Device location </td>
                            <td>NO</td>
                        </tr>
                        <tr>
                            <td>H. Audio, electronic, sensory, or
similar information</td>
                            <td>Images and audio, video or call recordings created
in connection with our business activities </td>
                            <td>NO</td>
                        </tr>
                        <tr>
                            <td>I. Professional or employment-
related information</td>
                            <td>Business contact details in order to provide you our
Services at a business level or job title, work history,
and professional qualifications if you apply for a job
with us</td>
                            <td>YES</td>
                        </tr>
                        <tr>
                            <td>J. Education information</td>
                            <td>Student records and directory information</td>
                            <td>YES</td>
                        </tr>
                        <tr>
                            <td>K. Inferences drawn from
collected personal information</td>
                            <td>Inferences drawn from any of the collected personal
information listed above to create a profile or
summary about, for example, an individual's
preferences and characteristics</td>
                            <td>YES</td>
                        </tr>
                        <tr>
                            <td>L. Sensitive personal information</td>
                            <td></td>
                            <td>NO</td>
                        </tr>
                    </table>
                    <p>We may also collect other personal information outside of these categories through instances where
you interact with us in person, online, or by phone or mail in the context of:</p>
                    <ul className="list-outside list-disc ml-8 space-y-4">
                        <li>Receiving help through our customer support channels;</li>
                        <li>Participation in customer surveys or contests; and</li>
                        <li>Facilitation in the delivery of our Services and to respond to your inquiries.</li>
                    </ul>
                    <p>We will use and retain the collected personal information as needed to provide the Services or for:</p>
                    <ul className="list-outside list-disc ml-8 space-y-4">
                        <li>Category H - 6 months</li>
                        <li>Category I - 6 months</li>
                        <li>Category J - 6 months</li>
                        <li>Category K - 6 months</li>
                    </ul>
                    </div>
                    
                    <h4 className="font-bold text-xl">Sources of Personal Information</h4>
                    
                    <p className="opacity-80">Learn more about the sources of personal information we collect in "WHAT INFORMATION DO
WE COLLECT?"</p>
                    <h4 className="font-bold text-xl">How We Use and Share Personal Information</h4>
                    <div className="opacity-80 space-y-4">
                        <p>
                        Learn more about how we use your personal information in the section, "HOW DO WE
PROCESS YOUR INFORMATION?"
                    </p>
                    <p className="font-bold">Will your information be shared with anyone else?</p>
                    <p>
                        We may disclose your personal information with our service providers pursuant to a written contract
between us and each service provider. Learn more about how we disclose personal information to in
the section, "WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL
INFORMATION?"
                    </p>
                    <p>We may use your personal information for our own business purposes, such as for undertaking
internal research for technological development and demonstration. This is not considered to
be "selling" of your personal information.</p>
                    <p>We have not disclosed, sold, or shared any personal information to third parties for a business or
commercial purpose in the preceding twelve (12) months. We will not sell or share personal
information in the future belonging to website visitors, users, and other consumers.</p>
                    </div>
                    
                    <h4 className="font-bold text-xl">Your Rights</h4>
                    <div className="opacity-80 space-y-4">
                        <p>
                        You have rights under certain US state data protection laws. However, these rights are not absolute,
                        and in certain cases, we may decline your request as permitted by law. These rights include:
                    </p>
                    <ul className="list-outside list-disc ml-8 space-y-4">
                        <li>
                            <b>Right to know</b> whether or not we are processing your personal data
                        </li>
                        <li>
                            <b>Right to access</b> your personal data
                        </li>
                        <li>
                            <b>Right to correct</b> inaccuracies in your personal data
                        </li>
                        <li>
                            <b>Right to request</b> the deletion of your personal data
                        </li>
                        <li>
                            <b>Right to obtain</b> a copy of the personal data you previously shared with us
                        </li>
                        <li>
                            <b>Right to non-discrimination</b> for exercising your rights
                        </li>
                        <li>
                            <b>Right to opt out</b> of the processing of your personal data if it is used for targeted
                            advertising (or sharing as defined under California’s privacy law), the sale of
                            personal data, or profiling in furtherance of decisions that produce legal or similarly
                            significant effects ("profiling")
                        </li>
                    </ul>
                    <p>Depending upon the state where you live, you may also have the following rights:</p>
                    <ul className="list-outside list-disc ml-8 space-y-4">
                        <li>
                            Right to access the categories of personal data being processed (as permitted by
                            applicable law, including the privacy law in Minnesota)
                        </li>
                        <li>
                            Right to obtain a list of the categories of third parties to which we have disclosed
                            personal data (as permitted by applicable law, including the privacy law in California,
                            Delaware, and Maryland)
                        </li>
                        <li>
                            Right to obtain a list of specific third parties to which we have disclosed personal
                            data (as permitted by applicable law, including the privacy law in Minnesota and
                            Oregon)
                        </li>
                        <li>
                            Right to review, understand, question, and correct how personal data has been
                            profiled (as permitted by applicable law, including the privacy law in Minnesota)
                        </li>
                        <li>
                            Right to limit use and disclosure of sensitive personal data (as permitted by
                            applicable law, including the privacy law in California)
                        </li>
                        <li>
                            Right to opt out of the collection of sensitive data and personal data collected
                            through the operation of a voice or facial recognition feature (as permitted by
                            applicable law, including the privacy law in Florida)
                        </li>
                    </ul>
                    </div>
                    
                    <h4 className="font-bold text-xl">How to Exercise Your Rights</h4>
                    
                    <div className="opacity-80 space-y-4">
                        <p>
                        To exercise these rights, you can contact us by submitting a data subject access request, by
                        emailing us at admin@boundlessgamers.org, by visiting http://www.boundlessgamers.org/contact, or
                        by referring to the contact details at the bottom of this document.
                    </p>
                    <p>
                        We will honor your opt-out preferences if you enact the Global Privacy Control (GPC) opt-out
                        signal on your browser.
                    </p>
                    <p>
                        Under certain US state data protection laws, you can designate an authorized agent to make a
                        request on your behalf. We may deny a request from an authorized agent that does not submit proof
                        that they have been validly authorized to act on your behalf in accordance with applicable laws.
                    </p>
                    </div>
                    
                    <h4 className="font-bold text-xl">Request Verification</h4>
                    <div className="opacity-80 space-y-4">
                        <p>Upon receiving your request, we will need to verify your identity to determine you are the same
                        person about whom we have the information in our system. We will only use personal information
                        provided in your request to verify your identity or authority to make the request. However, if we
                        cannot verify your identity from the information already maintained by us, we may request that you
                        provide additional information for the purposes of verifying your identity and for security or fraud-
                        prevention purposes.
                    </p>
                    <p>
                        If you submit the request through an authorized agent, we may need to collect additional information
                        to verify your identity before processing your request and the agent will need to provide a written and
                        signed permission from you to submit such request on your behalf.
                    </p>
                    </div>
                    
                    <h4 className="font-bold text-xl">Appeals</h4>
                    <p className="opacity-80">
                        Under certain US state data protection laws, if we decline to take action regarding your request, you
                        may appeal our decision by emailing us at admin@boundlessgamers.org. We will inform you in
                        writing of any action taken or not taken in response to the appeal, including a written explanation of
                        the reasons for the decisions. If your appeal is denied, you may submit a complaint to your state
                        attorney general.
                    </p>
                    <h4 className="font-bold text-xl">California "Shine The Light" Law</h4>
                    <p className="opacity-80">
                        California Civil Code Section 1798.83, also known as the "Shine The Light" law, permits our users
                        who are California residents to request and obtain from us, once a year and free of charge,
                        information about categories of personal information (if any) we disclosed to third parties for direct
                        marketing purposes and the names and addresses of all third parties with which we shared personal
                        information in the immediately preceding calendar year. If you are a California resident and would
                        like to make such a request, please submit your request in writing to us by using the contact details
                        provided in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?"
                    </p>
                    <h3 className="font-bold text-2xl">12. DO WE MAKE UPDATES TO THIS NOTICE?</h3>
                    
                    <div className="opacity-80 space-y-4">
                        <p className="italic">
                        <b>In Short:</b>
                         Yes, we will update this notice as necessary to stay compliant with relevant laws.
                    </p>
                    <p>
                        We may update this Privacy Notice from time to time. The updated version will be indicated by an
                        updated "Revised" date at the top of this Privacy Notice. If we make material changes to this Privacy
                        Notice, we may notify you either by prominently posting a notice of such changes or by directly
                        sending you a notification. We encourage you to review this Privacy Notice frequently to be informed
                        of how we are protecting your information.
                    </p>
                    </div>
                    
                    <h3 className="font-bold text-2xl">13. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h3>
                    <div className="opacity-80 space-y-4">
                        <p>
                        If you have questions or comments about this notice, you may email us
                        at admin@boundlessgamers.org or contact us by post at:
                    </p>
                    <p>
                        Boundless Gamers<br/>
                        14082 Lawrence 2235<br/>
                        Verona, MO 65769<br/>
                        United States
                    </p>
                    </div>
                    
                    <h3 className="font-bold text-2xl">14. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</h3>
                    <div className="opacity-80 space-y-4">
                        <p>
                        Based on the applicable laws of your country or state of residence in the US, you may have the right
                        to request access to the personal information we collect from you, details about how we have
                        processed it, correct inaccuracies, or delete your personal information. You may also have the right
                        to withdraw your consent to our processing of your personal information. These rights may be limited
                        in some circumstances by applicable law. To request to review, update, or delete your personal
                        information, please fill out and submit a data subject access request.
                    </p>
                    </div>
                    
                </div>
                    </section>
        </>
    );
}

export default Privacy;